import Vue from 'vue';
import Router from 'vue-router';
import Leads from '@/views/Leads.vue';
import Case from '@/views/Case.vue';
import Dashboard from '@/views/Dashboard.vue';
import Account from '@/views/Account.vue';
import Prospecting from '@/views/Prospecting.vue';
import Prospect from '@/views/Prospect.vue';
import LeadCohorts from '@/views/LeadCohorts.vue';
import Lead from '@/views/Lead.vue';
import ListingReferrals from '@/views/ListingReferrals.vue';
import SignUp from '@/views/SignUp.vue';
import Navigation from '@/views/partials/Navigation.vue';
import ContactUs from '@/views/ContactUs.vue';
import ProspectingFileUpload from '@/views/ProspectingFileUpload.vue'
import analyticsHelpers from './helpers/analytics-helpers';
import cookieHelpers from './helpers/cookie-helpers';
import commonHelpers from '@/helpers/common-helpers';

const NotFound = () => import(/* webpackChunkName: "below-fold" */ '@/views/NotFound.vue');
const Login = () => import(/* webpackChunkName: "below-fold" */ '@/views/Login.vue');
const IncompatibleBrowser = () => import(/* webpackChunkName: "about" */ '@/views/IncompatibleBrowser.vue');

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/',
      redirect: () => ({ name: 'login' }),
    },
    {
      path: '/:uk/case/:ck',
      name: 'case',
      components: {
        default: Case
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/case',
      },
    },
    {
      path: '/:ak/account/:uk?',
      name: 'account',
      components: {
        default: Account,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/account',
        requiresAuth: true,
      },
    },
    {
      path: '/:ak?/dashboard/:uk',
      name: 'dashboard',
      components: {
        default: Dashboard,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/dashboard',
      },
    },
    {
      path: '/:ak/prospecting-signup',
      name: 'plugnplay',
      redirect: '/:ak/prospecting',
    },
    {
      path: '/:ak/prospecting',
      name: 'prospecting',
      components: {
        default: Prospecting,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/prospecting',
      },
    },
    {
      path: '/:ak?/prospect/:pk',
      name: 'prospect',
      components: {
        default: Prospect,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/prospect',
      },
    },
    {
      path: '/:ak?/leads/:uk?',
      name: 'leads',
      components: {
        default: Leads,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/leads/list',
      },
    },
    {
      path: '/leadcohorts/:uk',
      name: 'leadcohorts',
      components: {
        default: LeadCohorts,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/leadcohorts',
      },
    },
    {
      path: '/:ak/lead/:lk/:uk?',
      name: 'lead',
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      components: {
        default: Lead,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/leads/referral',
        requiresBrowserCheck: true,
      },
    },
    {
      path: '/listing-referrals/:uk',
      name: 'listingreferrals',
      components: {
        default: ListingReferrals,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/listingreferrals',
      },
    },
    {
      path: '/:ak/incompatible-browser',
      name: 'incompatible-browser',
      components: {
        default: IncompatibleBrowser,
        navigation: Navigation,
      },
      props: { default: true },
    },
    {
      path: '/:ak/contact',
      name: 'contact',
      components: {
        default: ContactUs,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        gaPageView: '/agentportal/contact',
      },
    },
    {
      path: '/sign-up/:ak/:st',
      name: 'signup',
      components: {
        default: SignUp,
        navigation: Navigation,
      },
      props: true,
    },
    {
      path: '/:ak/prospect-file-upload',
      name: 'prospect-file-upload',
      components: {
        default: ProspectingFileUpload,
        navigation: Navigation,
      },
      props: { default: true },
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      props: true,
    },
    {
      path: '/reset-password/:rt/:sr',
      name: 'reset-password',
      component: Login,
      props: true,
    },
    {
      path: '/404',
      name: '404',
      components: {
        default: NotFound,
        navigation: Navigation,
      },
      props: true,
    },
    {
      path: '*',
      name: 'error',
      redirect: (to) => {
        // Redirects from old Agent Portal to new
        switch (to.path) {
          // Account section
          case '/main.php':
            if (to.query.tab === 'reviews') {
              return { name: 'login', query: { redirect: 'account', tab: '#your-reviews' } };
            } if (to.query.tab === 'mycontactdetails') {
              return { name: 'login', query: { redirect: 'account' } };
            } if (to.query.tab === 'soldproperties') {
              return { name: 'login', query: { redirect: 'account', tab: '#your-sales' } };
            }
            return { name: 'login', query: { redirect: 'leads' } };
          // Single Deal Page (SDP)
          case '/response.php':
            return { name: 'lead', params: { ak: to.query.ak, lk: to.query.lk }, query: null };
          // Login
          case '/login.php':
            return { name: 'login' };
          case '/forgot-password.php':
            return { name: 'login' };
          case '/reset-password.php':
            return { name: 'reset-password', params: { rt: to.query.rt, sr: to.query.sr } };
          case '/signup.php':
            return { name: 'signup', params: { ak: to.query.ak, st: to.query.st } };
          // Premier Contract
          case '/premier-signup.php':
            window.location = `${process.env.VUE_APP_OA_URL}/premier-contract/sign-up?ck=${to.query.ck}`;
            break;
          case '/premier-update.php':
            window.location = `${process.env.VUE_APP_OA_URL}/premier-contract/update?ck=${to.query.ck}`;
            break;
          default:
            return { path: '/404' };
        }

        return { path: '/404' };
      },
    },

  ],
});

function isAuthenticated(uk, ak) {
  const userToken = cookieHelpers.getCookie('user-token') ? cookieHelpers.getCookie('user-token') : false;
  const logedInUserKey = cookieHelpers.getCookie('user-key');
  const logedInAgentKey = cookieHelpers.getCookie('agent-key');
  let isloggedInUser = false;

  if ((logedInUserKey && uk === logedInUserKey) || (logedInAgentKey && !uk && ak === logedInAgentKey)) {
    isloggedInUser = true;
  }

  console.log(`isAuthenticated = ${userToken && isloggedInUser}`);

  return userToken && isloggedInUser;
}

router.beforeEach((to, from, next) => {
  // Fix for an Agent who got sent the wrong link
  if (to.params.ak === '6c1dface-94e4-412f-b59e-6b1d128eca83' && to.params.lk === '9efcde8b-9a11-4830-9102-414545a31e4f') {
    next({
      path: '/',
      replace: true,
    });
  }

  if (to.meta) {
    // Check if we are actually changing routes and there is a google Analytics page view avalaible
    if (from.name !== to.name && to.meta.gaPageView) {
      analyticsHelpers.gaPageView(to.meta.gaPageView);
    }

    if (to.meta.requiresBrowserCheck === true && commonHelpers.isUnsupportedBrowser()) {
      next({ name: 'incompatible-browser' });
    } else if (to.meta.requiresAuth && !isAuthenticated(to.params.uk, to.params.ak)) {
      if (from.name === 'login') {
        next({
          path: '/',
          replace: true,
        });
      } else {
        next({
          name: 'login',
          query: { redirect: to.name, tab: to.hash ? to.hash : '' },
          params: { ak: to.params.ak, uk: to.params.uk },
        });
      }

      if (to.name === 'account') {
        window.location = `${window.location.origin}/login?redirect=account&tab=${encodeURIComponent(to.hash)}`;
      }
    }
  }
  next();
});

export default router;
